import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Sparkles, Search, Loader2, Check, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useUserData } from '../../lib/auth';
import { useOrgAiEnabled, AI_DISABLED_TOOLTIP, AI_DISABLED_MESSAGE, aiDisabledFromInvokeError } from '../../lib/hooks/useOrgAiEnabled';

/**
 * Azioni secondarie della checklist (spunti Trello, partner Unidevs):
 * - "Copia da un'altra task": riusa le voci di una checklist esistente dell'organizzazione
 * - "Genera con AI": propone i passi a partire da titolo e descrizione della task
 * In entrambi i casi l'utente vede l'anteprima e conferma prima che venga scritto qualcosa.
 */

export interface ChecklistDraftItem {
  title: string;
  is_required: boolean;
}

interface TaskChecklistActionsProps {
  taskId: string;
  taskTitle: string;
  taskDescription?: string;
  projectName?: string;
  existingTitles: string[];
  onAddItems: (items: ChecklistDraftItem[]) => Promise<void>;
}

interface SourceTask {
  id: string;
  title: string;
  projectName: string | null;
  items: ChecklistDraftItem[];
}

type Panel = 'copy' | 'ai' | null;

const linkClass =
  'inline-flex items-center gap-1 text-xs text-[#9B9A97] dark:text-gray-500 hover:text-[#37352F] dark:hover:text-gray-200 transition-colors';

export function TaskChecklistActions({
  taskId,
  taskTitle,
  taskDescription,
  projectName,
  existingTitles,
  onAddItems,
}: TaskChecklistActionsProps) {
  const { organizationId } = useUserData();
  const [panel, setPanel] = useState<Panel>(null);
  const [saving, setSaving] = useState(false);

  // Copia da un'altra task
  const [sources, setSources] = useState<SourceTask[] | null>(null);
  const [loadingSources, setLoadingSources] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedSource, setSelectedSource] = useState<SourceTask | null>(null);

  // Genera con AI (spento dall'interruttore IA dell'azienda: titolo e descrizione sono dati di progetto)
  const aiEnabled = useOrgAiEnabled();
  const [generating, setGenerating] = useState(false);
  const [proposals, setProposals] = useState<ChecklistDraftItem[]>([]);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    if (panel !== 'copy' || sources || !organizationId) return;
    let cancelled = false;
    setLoadingSources(true);
    (async () => {
      // Le voci vengono raggruppate per task lato client: una sola query, niente RPC
      const { data, error } = await supabase
        .from('task_checklist')
        .select('task_id, title, is_required, sort_order, task:tasks!inner(id, title, project:projects(name))')
        .eq('organization_id', organizationId)
        .neq('task_id', taskId)
        .order('sort_order', { ascending: true })
        .limit(1000);
      if (cancelled) return;
      if (error) {
        console.error('[TaskChecklistActions] caricamento checklist:', error);
        setSources([]);
      } else {
        const map = new Map<string, SourceTask>();
        (data || []).forEach((row: any) => {
          const task = Array.isArray(row.task) ? row.task[0] : row.task;
          if (!task) return;
          const entry = map.get(row.task_id) || {
            id: row.task_id,
            title: task.title,
            projectName: task.project?.name || null,
            items: [],
          };
          entry.items.push({ title: row.title, is_required: !!row.is_required });
          map.set(row.task_id, entry);
        });
        setSources([...map.values()].sort((a, b) => a.title.localeCompare(b.title, 'it')));
      }
      setLoadingSources(false);
    })();
    return () => { cancelled = true; };
  }, [panel, sources, organizationId, taskId]);

  const filteredSources = useMemo(() => {
    if (!sources) return [];
    const q = search.trim().toLowerCase();
    if (!q) return sources.slice(0, 30);
    return sources
      .filter(s => s.title.toLowerCase().includes(q) || (s.projectName || '').toLowerCase().includes(q))
      .slice(0, 30);
  }, [sources, search]);

  const close = () => {
    setPanel(null);
    setSelectedSource(null);
    setSearch('');
    setProposals([]);
    setChecked(new Set());
    setAiError(null);
  };

  const handleGenerate = async () => {
    if (!aiEnabled) return;
    setGenerating(true);
    setAiError(null);
    setProposals([]);
    try {
      const { data, error } = await supabase.functions.invoke('ai-checklist-generate', {
        body: { title: taskTitle, description: taskDescription || '', project_name: projectName || '', existing: existingTitles },
      });
      if (error) throw error;
      const items: ChecklistDraftItem[] = Array.isArray(data?.items) ? data.items : [];
      if (items.length === 0) {
        setAiError('Nessuna proposta: prova ad arricchire la descrizione della task.');
      }
      setProposals(items);
      setChecked(new Set(items.map((_, i) => i)));
    } catch (err) {
      console.error('[TaskChecklistActions] generazione AI:', err);
      setAiError((await aiDisabledFromInvokeError(err)) ? AI_DISABLED_MESSAGE : 'Generazione non riuscita, riprova tra poco.');
    } finally {
      setGenerating(false);
    }
  };

  const confirm = async (items: ChecklistDraftItem[]) => {
    if (items.length === 0) return;
    setSaving(true);
    try {
      await onAddItems(items);
      close();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-1 pl-9">
      {/* Azioni in linea, discrete come i link secondari di Notion */}
      {panel === null && (
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setPanel('copy')} className={linkClass}>
            <Copy className="h-3 w-3" />
            Copia da un'altra task
          </button>
          <button
            type="button"
            onClick={() => { setPanel('ai'); void handleGenerate(); }}
            className={`${linkClass} disabled:opacity-50 disabled:cursor-not-allowed`}
            disabled={!aiEnabled}
            title={aiEnabled ? undefined : AI_DISABLED_TOOLTIP}
          >
            <Sparkles className="h-3 w-3" />
            Genera con AI
          </button>
        </div>
      )}

      {panel !== null && (
        <div className="mt-1 rounded-md border border-[rgba(55,53,47,0.09)] dark:border-gray-700 bg-white dark:bg-gray-900 p-2">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-[#37352F] dark:text-gray-100 inline-flex items-center gap-1">
              {panel === 'copy' ? <Copy className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
              {panel === 'copy' ? 'Copia checklist da…' : 'Checklist proposta dall\'AI'}
            </span>
            <button type="button" onClick={close} className="p-0.5 text-[#9B9A97] hover:text-[#37352F] dark:hover:text-gray-200" title="Chiudi">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {panel === 'copy' && !selectedSource && (
            <>
              <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-[rgba(55,53,47,0.16)] dark:border-gray-600 bg-[#F7F7F5] dark:bg-gray-800">
                <Search className="h-3 w-3 text-[#9B9A97]" />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cerca task o progetto…"
                  className="flex-1 text-xs bg-transparent outline-none text-[#37352F] dark:text-gray-100 placeholder:text-[#9B9A97]"
                />
              </div>
              <div className="mt-1 max-h-44 overflow-y-auto">
                {loadingSources && (
                  <div className="flex items-center gap-1.5 px-2 py-2 text-xs text-[#9B9A97]"><Loader2 className="h-3 w-3 animate-spin" /> Carico le checklist…</div>
                )}
                {!loadingSources && filteredSources.length === 0 && (
                  <div className="px-2 py-2 text-xs text-[#9B9A97]">Nessuna task con checklist trovata.</div>
                )}
                {filteredSources.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSelectedSource(s)}
                    className="w-full flex items-center justify-between gap-2 px-2 py-1 rounded text-left hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-800"
                  >
                    <span className="min-w-0">
                      <span className="block text-xs text-[#37352F] dark:text-gray-100 truncate">{s.title}</span>
                      {s.projectName && <span className="block text-[11px] text-[#9B9A97] truncate">{s.projectName}</span>}
                    </span>
                    <span className="text-[11px] text-[#9B9A97] flex-shrink-0">{s.items.length} voci</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {panel === 'copy' && selectedSource && (
            <>
              <div className="text-[11px] text-[#9B9A97] mb-1">
                Da <span className="text-[#37352F] dark:text-gray-200">{selectedSource.title}</span>
              </div>
              <PreviewList items={selectedSource.items} />
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void confirm(selectedSource.items)}
                  className="px-2.5 py-1 text-xs font-medium bg-[#2383E2] text-white rounded hover:bg-[#1a6dc0] disabled:opacity-50"
                >
                  {saving ? 'Copio…' : `Copia ${selectedSource.items.length} voci`}
                </button>
                <button type="button" onClick={() => setSelectedSource(null)} className="text-xs text-[#787774] hover:text-[#37352F] dark:hover:text-gray-200">
                  Scegli un'altra task
                </button>
              </div>
            </>
          )}

          {panel === 'ai' && (
            <>
              {generating && (
                <div className="flex items-center gap-1.5 px-1 py-2 text-xs text-[#9B9A97]"><Loader2 className="h-3 w-3 animate-spin" /> Sto leggendo la task e preparo i passi…</div>
              )}
              {aiError && <div className="px-1 py-1 text-xs text-[#EB5757]">{aiError}</div>}
              {!generating && proposals.length > 0 && (
                <>
                  <div className="max-h-48 overflow-y-auto">
                    {proposals.map((p, i) => (
                      <label key={i} className="flex items-center gap-2 px-1 py-1 rounded cursor-pointer hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-800">
                        <button
                          type="button"
                          onClick={() => setChecked(prev => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n; })}
                          className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${checked.has(i) ? 'bg-[#2383E2] border-[#2383E2]' : 'border-[#C4C4C4] dark:border-gray-500'}`}
                        >
                          {checked.has(i) && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                        </button>
                        <span className="text-xs text-[#37352F] dark:text-gray-100">
                          {p.title}
                          {p.is_required && <span className="ml-1 text-[#EB5757]">*</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      type="button"
                      disabled={saving || checked.size === 0}
                      onClick={() => void confirm(proposals.filter((_, i) => checked.has(i)))}
                      className="px-2.5 py-1 text-xs font-medium bg-[#2383E2] text-white rounded hover:bg-[#1a6dc0] disabled:opacity-50"
                    >
                      {saving ? 'Aggiungo…' : `Aggiungi ${checked.size} voci`}
                    </button>
                    <button type="button" onClick={() => void handleGenerate()} disabled={!aiEnabled} title={aiEnabled ? undefined : AI_DISABLED_TOOLTIP} className="text-xs text-[#787774] hover:text-[#37352F] dark:hover:text-gray-200 disabled:opacity-50">
                      Rigenera
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PreviewList({ items }: { items: ChecklistDraftItem[] }) {
  return (
    <div className="max-h-40 overflow-y-auto">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2 px-1 py-0.5 text-xs text-[#37352F] dark:text-gray-100">
          <span className="w-3.5 h-3.5 rounded border border-[#C4C4C4] dark:border-gray-500 flex-shrink-0" />
          <span className="truncate">{it.title}{it.is_required && <span className="ml-1 text-[#EB5757]">*</span>}</span>
        </div>
      ))}
    </div>
  );
}
